import { Typography } from "@mui/material";
import { get } from "lodash";
import React from "react";
import { connect } from "react-redux";
import { RouteComponentProps } from "react-router";
import AppBar from "src/components/AppBar/AppBar";
import PromptModal from "src/components/ConfirmationModal/PromptModalV2";
import FinalTableSpy from "src/components/FinalTable/FinalTableSpy";
import GuardedComponent from "src/components/GuardedComponent/GuardedComponent";
import Icon, { IconName } from "src/components/Icon/Icon";
import StatusFilter from "src/components/TableV2/StatusFilter";
import { LoadingContextType, withLoading } from "src/hooks/useLoading";
import { UserRole } from "src/store/models/user.model";
import { fetchBrokerOptions } from "src/store/reducers/brokerBuilder";
import { hasAnyAuthority } from "src/util/roleAuthorization";
import { openUrl } from "src/util/util.navigation";
import FilterContainer from "../../components/TableV2/FilterContainer";
import SearchFieldTool from "../../components/TableV2/SearchField";
import DisTable from "../../components/TableV2/Table";
import { TableActions } from "../../components/TableV2/TableActions";
import Title from "../../components/TableV2/Title";
import * as S from "../../constants/StringConstants";
import { IBroker } from "../../store/models/broker.model";
import { IRootState } from "../../store/reducers";
import { fetchBrokersList, removeBroker } from "../../store/reducers/brokers";
import "./Brokers.scss";

interface IBrokersProps extends StateProps, DispatchProps, RouteComponentProps, LoadingContextType {}

interface IBrokerState {
  deletedId: number | null;
}

class Brokers extends React.Component<IBrokersProps, IBrokerState> {
  constructor(props: IBrokersProps) {
    super(props);
    this.state = {
      deletedId: null,
    };
    this.handleDeleteBrokerConfirm = this.handleDeleteBrokerConfirm.bind(this);
    this.handleDeleteBrokerCancel = this.handleDeleteBrokerCancel.bind(this);
  }

  componentDidMount() {
    this.props.fetchBrokerOptions();
  }

  handleDeleteBrokerPopup = (brokerID: number) => {
    this.setState({ deletedId: brokerID });
  };

  async handleDeleteBrokerConfirm() {
    if (this.state.deletedId !== null) {
      this.props.setIsLoading(true);
      await this.props.removeBroker(this.state.deletedId);
      await this.props.fetchBrokersList();
      this.setState({ deletedId: null });
      this.props.setIsLoading(false);
    }
  }

  handleDeleteBrokerCancel() {
    this.setState({ deletedId: null });
  }

  render() {
    const {
      auth,
      fetchBrokersList,
      brokers: { map, projection, total, listOptions },
      brokerBuilder: { branchList, companyList, affiliateList },
    } = this.props;
    const isAdmin = hasAnyAuthority(auth.user, [UserRole.Admin]);
    const columns = [
      {
        key: "brokerName",
        title: S.BRO_COL_NAME,
        render: item => {
          if (item.fromBrokerQuoteEngine) {
            return <div className="fromBrokerQuoteEngine">{item.brokerName}</div>;
          } else {
            return item.brokerName;
          }
        },
        style: { minWidth: "12rem", width: "20rem" },
        sortKey: "BrokerName",
      },
      {
        key: "primaryPhone",
        title: S.BRO_COL_PRIMARY_PHONE,
        style: { minWidth: "15rem" },
      },
      {
        key: "primaryEmail",
        title: S.BRO_COL_PRIMARY_EMAIL,
        style: { paddingRight: "5rem" },
      },
      {
        key: "companyName",
        title: S.BRO_COL_COMPANY,
        style: { minWidth: "15rem" },
        sortKey: "CompanyName",
      },
      {
        key: "affiliateName",
        title: S.BRO_COL_AFFILIATE,
        style: { minWidth: "15rem" },
        sortKey: "AffiliateName",
      },
      {
        key: "branchName",
        title: S.BRO_COL_BRANCH,
        style: { minWidth: "10rem" },
        sortKey: "BranchName",
      },
      ...(isAdmin
        ? [
            {
              key: "actions",
              title: S.BRO_COL_ACTIONS,
              style: { minWidth: "8rem" },
              render: (item: IBroker) => {
                return item.deleted ? (
                  <span>{S.BRO_DELETED}</span>
                ) : (
                  <TableActions
                    actions={[
                      {
                        label: S.BRO_DELETE,
                        handler: () => this.handleDeleteBrokerPopup(item.brokerID),
                      },
                    ]}
                  />
                );
              },
            },
          ]
        : []),
    ];

    return (
      <div className="table-view-v2__content">
        <div className="table-view__header-buttons-container">
          <AppBar icon={IconName.Brokers} title={S.BRO_PAGE_TITLE} loading={this.props.isLoading}>
            <div className="table-view__buttons-container">
              <GuardedComponent
                authorizedRoles={[
                  UserRole.Admin,
                  UserRole.ProposalSpecialist,
                  UserRole.RegionalBranchManager,
                  UserRole.BranchManager,
                  UserRole.UnderwritingAssociate,
                ]}>
                <button className="side-nav__add-button" onClick={() => openUrl("/brokers/new")}>
                  <Icon name={IconName.Plus} />
                  {S.NEW_BROKER}
                </button>
              </GuardedComponent>
            </div>
          </AppBar>
        </div>

        <DisTable<IBroker>
          columns={columns}
          initialState={{ rows: 25, map, projection, total, ...listOptions }}
          onRowClick={(data: IBroker) => {
            this.props.history.push(`/brokers/${data.brokerID}/details`);
          }}
          onUpdate={async ({ page, rows, sort, query }) => {
            this.props.setIsLoading(true);
            const result: any = await fetchBrokersList({ page, rows, sort, query });
            this.props.setIsLoading(false);
            return {
              total: get(result, "value.data.totalRows") as number,
              data: get(result, "value.data.items") as IBroker[],
            };
          }}>
          <FilterContainer
            left={<Title text={S.BRO_PAGE_TITLE} />}
            right={
              <>
                <SearchFieldTool />
                <StatusFilter
                  key="companyId"
                  fieldName="companyId"
                  fieldLabel={S.BRO_FILTERS_COMPANY}
                  statusList={companyList}
                />
                <StatusFilter
                  key="branchId"
                  fieldName="branchId"
                  fieldLabel={S.BRO_FILTERS_BRANCH}
                  statusList={branchList}
                />
                <StatusFilter
                  key="affiliateId"
                  fieldName="affiliateId"
                  fieldLabel={S.BRO_FILTERS_AFFILIATE}
                  statusList={affiliateList}
                />
                <FinalTableSpy>
                  {props => (
                    <button className="button-v2 gray small" onClick={() => props.update({ page: 1, query: {} })}>
                      {S.APP_FILTERS_RESET}
                    </button>
                  )}
                </FinalTableSpy>
              </>
            }></FilterContainer>
        </DisTable>

        {this.state.deletedId !== null && (
          <PromptModal
            open={this.state.deletedId !== null}
            title={S.BRO_DELETE_CONFIRM_TITLE}
            confirmText={S.BRO_DELETE_CONFIRM_CONFIRM}
            rejectText={S.BRO_DELETE_CONFIRM_REJECT}
            onConfirm={this.handleDeleteBrokerConfirm}
            onReject={this.handleDeleteBrokerCancel}>
            <Typography variant="h6">{S.BRO_DELETE_CONFIRM_MESSAGE}</Typography>
          </PromptModal>
        )}
      </div>
    );
  }
}

const mapStateToProps = ({ brokers, auth, brokerBuilder }: IRootState) => ({ brokers, brokerBuilder, auth });
const mapDispatchToProps = { fetchBrokersList, fetchBrokerOptions, removeBroker };

type StateProps = ReturnType<typeof mapStateToProps>;
type DispatchProps = typeof mapDispatchToProps;

export default withLoading(connect(mapStateToProps, mapDispatchToProps)(Brokers));
